import { useCallback, useEffect, useState } from "react";
import { Button, Flex, Text, Textarea } from "@mantine/core";
import styled from "styled-components";
import dayjs from "dayjs";
import {
  approveCorrectionRequest,
  getCorrectionRequests,
  rejectCorrectionRequest,
} from "../../api/reportsApi";
import DataTable, { Column } from "../common/Table/DataTable";
import CustomSelect from "../common/Inputs/CustomSelect";
import { EmployeeCell } from "./ReportCells";
import {
  createNotification,
  errorNotification,
} from "../../utils/useNotification";

/**
 * ÇALIŞAN BİLDİRİMLERİ — İK'nın iş kuyruğu (plan 2.50).
 *
 * ⚠ BU BİR İTİRAZ KUYRUĞU DEĞİL. Çalışan İK'nın yaptığı bir şeye itiraz
 * etmiyor; kendi unuttuğu çıkışı bildiriyor ("şu saatte çıkmıştım").
 *
 * ⚠ VE BİLDİRİM DÜZELTMEYİ OTOMATİK OLUŞTURMAZ. "Kabul et" düğmesi bildirimi
 * 2.49-B düzeltmesine çeviren yerdir — ama o tık bir İNSANIN tıkıdır. Otomatik
 * olsaydı çalışan kendi saatini kendi yazmış olurdu ve iki katmanlı yapının
 * (ölçüm / beyan / karar) bütün anlamı kaçardı.
 *
 * ⚠ Kabul anında doğrulama kuralları sunucuda YENİDEN koşuluyor: aradan geçen
 * sürede çalışan o günün çıkışını okutmuş (geç senkronlanan çevrimdışı makbuz)
 * ya da İK günü elden düzeltmiş olabilir. Bu yüzden "Kabul et" 400 dönebilir ve
 * mesajı olduğu gibi gösteriliyor.
 */

const DURUM_ETIKET: Record<string, string> = {
  pending: "Bekliyor",
  approved: "Kabul edildi",
  rejected: "Kabul edilmedi",
};

const DURUM_SECENEK = [
  { value: "pending", label: DURUM_ETIKET.pending },
  { value: "approved", label: DURUM_ETIKET.approved },
  { value: "rejected", label: DURUM_ETIKET.rejected },
];

const saat = (value?: string | null) =>
  value ? dayjs(value).format("HH:mm") : "—";

const CorrectionRequestQueue = () => {
  // Varsayılan "bekliyor": bu ekran bir İŞ kuyruğu, arşiv değil.
  const [status, setStatus] = useState<string | undefined>("pending");
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [redNotu, setRedNotu] = useState<Record<string, string>>({});

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data }: any = await getCorrectionRequests(status);
      setRows(data?.data ?? []);
    } catch (error) {
      console.error("Bildirimler alınamadı:", error);
      setRows([]);
      errorNotification({
        title: "Bildirimler Yüklenemedi.",
        message: "Çalışan bildirimleri alınırken bir hata oluştu.",
      });
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const karar = async (row: any, kabul: boolean) => {
    setBusyId(row.id);
    try {
      if (kabul) {
        await approveCorrectionRequest(row.id);
        createNotification({
          success: true,
          message: "Bildirim düzeltmeye çevrildi.",
        });
      } else {
        await rejectCorrectionRequest(row.id, redNotu[row.id]?.trim() || undefined);
        createNotification({
          success: true,
          message: "Bildirim kabul edilmedi olarak işaretlendi.",
        });
      }
      await fetchRows();
    } catch (error: any) {
      errorNotification({
        title: "İşlem Tamamlanamadı.",
        message:
          error?.response?.data?.message ??
          "Bildirim işlenirken bir hata oluştu.",
      });
    } finally {
      setBusyId(null);
    }
  };

  const columns: Column[] = [
    {
      key: "employeeName",
      header: "Ad Soyad",
      width: "18%",
      render: (row) => <EmployeeCell name={row.employeeName} />,
    },
    {
      key: "workday",
      header: "İş Günü",
      width: "12%",
      render: (row) => (
        <Text fw={400} size="md" style={{ color: "#111827" }}>
          {dayjs(row.workday).format("DD.MM.YYYY")}
        </Text>
      ),
    },
    {
      key: "claimedExitAt",
      header: "Beyan edilen çıkış",
      width: "13%",
      render: (row) => (
        <Text fw={600} size="md" style={{ color: "#111827" }}>
          {saat(row.claimedExitAt)}
        </Text>
      ),
    },
    {
      // Çalışanın kendi cümlesi. Kabul edilirse gün satırındaki gerekçe bu
      // olur — İK'nın kendi notunu yazmasına gerek kalmıyor.
      key: "note",
      header: "Çalışanın notu",
      width: "27%",
      render: (row) => (
        <Text fw={400} size="sm" style={{ color: "#374151" }}>
          {row.note}
        </Text>
      ),
    },
    {
      key: "status",
      header: "Durum",
      width: "12%",
      render: (row) => (
        <StatusPill $status={row.status}>
          {DURUM_ETIKET[row.status] ?? row.status}
        </StatusPill>
      ),
    },
    {
      key: "actions",
      header: "",
      width: "18%",
      render: (row) =>
        row.status === "pending" ? (
          <Flex direction="column" gap="6px">
            <Textarea
              placeholder="Red gerekçesi (isteğe bağlı)"
              autosize
              minRows={1}
              size="xs"
              value={redNotu[row.id] ?? ""}
              onChange={(event) =>
                setRedNotu((prev) => ({
                  ...prev,
                  [row.id]: event.currentTarget.value,
                }))
              }
            />
            <Flex gap="6px">
              <Button
                size="xs"
                loading={busyId === row.id}
                onClick={() => karar(row, true)}>
                Kabul et
              </Button>
              <Button
                size="xs"
                variant="outline"
                color="red"
                loading={busyId === row.id}
                onClick={() => karar(row, false)}>
                Reddet
              </Button>
            </Flex>
          </Flex>
        ) : (
          <Text fw={400} size="sm" c="dimmed">
            {row.reviewNote || "—"}
          </Text>
        ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      loading={loading}
      rowKey={(row: any) => row.id}
      emptyText={
        status === "pending"
          ? "Bekleyen bildirim yok."
          : "Bu durumda bildirim yok."
      }
      toolbar={
        <CustomSelect
          placeholder="Durum"
          data={DURUM_SECENEK}
          value={status ?? null}
          clearable
          onChange={(value: any) => setStatus(value ?? undefined)}
        />
      }
    />
  );
};

export default CorrectionRequestQueue;

const StatusPill = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;

  ${({ $status }) => {
    if ($status === "approved") {
      return "color:#047857;background:#ecfdf5;border:1px solid #a7f3d0;";
    }
    if ($status === "rejected") {
      return "color:#dc2626;background:#fef2f2;border:1px solid #fecaca;";
    }
    return "color:#b45309;background:#fffbeb;border:1px solid #fde68a;";
  }}
`;
